
import { useState } from 'react';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const faqs = [
    {
      id: 1,
      question: "How much does professional Airbnb photo editing cost?",
      answer: "Our packages start at $250 for 1-5 photos and go up to $400 for 16-20 photos. For properties requiring 20+ photos, we provide custom quotes. Rush processing is available for an additional $50-100. Most hosts see ROI within 2-3 weeks through increased bookings.",
      category: "Pricing"
    },
    {
      id: 2,
      question: "What's the typical turnaround time for photo editing?",
      answer: "Standard turnaround is 3-5 business days. We offer rush services for urgent needs: 24-hour rush (+$50) and 12-hour rush (+$100). All edited photos are delivered via secure download link sent to your email.",
      category: "Process"
    },
    {
      id: 3,
      question: "What editing services are included in each package?",
      answer: "All packages include HDR processing, color correction, brightness/contrast optimization, perspective correction, and professional enhancement. We also remove minor imperfections, enhance lighting, and ensure consistent quality across all photos. Virtual staging elements can be added upon request.",
      category: "Services"
    },
    {
      id: 4,
      question: "How do I upload my photos after payment?",
      answer: "After completing payment, you'll receive a secure upload link via email within 15 minutes. Simply drag and drop your raw photos to the upload portal. We accept JPG, PNG, and RAW formats. Include any special instructions in the upload form.",
      category: "Process"
    },
    {
      id: 5,
      question: "What types of properties do you edit photos for?",
      answer: "We edit photos for all property types: residential homes, condos, apartments, luxury estates, commercial properties, vacation rentals, and land/lots. Our editing techniques are customized based on property type and target market.",
      category: "Services"
    },
    {
      id: 6,
      question: "Can you edit photos taken with a smartphone?",
      answer: "Yes! While professional camera photos yield the best results, we can significantly enhance smartphone photos. We recommend taking photos in good natural light and using your phone's HDR mode when available. Our editing can transform even basic smartphone photos into professional-looking images.",
      category: "Technical"
    },
    {
      id: 7,
      question: "Do you offer revisions if I'm not satisfied?",
      answer: "Absolutely! We offer one round of free revisions within 7 days of delivery. Simply let us know what adjustments you'd like, and we'll refine the photos to meet your expectations. Customer satisfaction is our top priority.",
      category: "Quality"
    },
    {
      id: 8,
      question: "How much can professional photos increase my Airbnb bookings?",
      answer: "Our clients typically see 45-120% increases in booking rates within 4-8 weeks. Average revenue increases range from $800-1,400 per month. Results vary based on location, property type, and market conditions, but 98% of our clients see positive ROI within 60 days.",
      category: "Results"
    },
    {
      id: 9,
      question: "What file formats and sizes do you deliver?",
      answer: "We deliver high-resolution JPG files optimized for web use (typically 1920x1080 or larger). Files are compressed for fast loading while maintaining professional quality. We can also provide specific sizes for different platforms upon request.",
      category: "Technical"
    },
    {
      id: 10,
      question: "Do you work with real estate agents and property managers?",
      answer: "Yes! We work with individual hosts, real estate agents, property management companies, and vacation rental businesses. We offer volume discounts for multiple properties and can establish ongoing partnerships for regular photo editing needs.",
      category: "Business"
    },
    {
      id: 11,
      question: "Can you remove or add furniture and decor in photos?",
      answer: "We can remove minor clutter and personal items, but extensive furniture removal or virtual staging requires our premium service. We can add basic decor elements like plants, artwork, or lighting effects. For major changes, we'll provide a custom quote.",
      category: "Services"
    },
    {
      id: 12,
      question: "How do I know if my photos need professional editing?",
      answer: "If your photos appear dark, have uneven lighting, look unprofessional compared to competitors, or your booking rate is below 60%, professional editing can help. We offer free photo assessments - just email us 2-3 sample photos for evaluation.",
      category: "Assessment"
    }
  ];

  const categories = ["All", "Pricing", "Process", "Services", "Technical", "Results", "Quality", "Business", "Assessment"];
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredFAQs = selectedCategory === "All" 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFAQ = (id: number) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about our professional Airbnb photo editing services
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {filteredFAQs.map((faq) => (
              <div key={faq.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full mr-3 font-medium">
                        {faq.category}
                      </span>
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    </div>
                    <i className={`ri-arrow-down-s-line text-gray-500 transition-transform ${
                      openFAQ === faq.id ? 'rotate-180' : ''
                    }`}></i>
                  </div>
                </button>
                {openFAQ === faq.id && (
                  <div className="px-6 py-4 bg-white border-t border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 text-center border border-blue-100">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Still Have Questions?</h3>
            <p className="text-gray-600 mb-6">
              Our team is here to help! Get personalized answers about your specific photo editing needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:(510) 641-8648"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (510) 641-8648
              </a>
              <a 
                href="mailto:info@vistaboostimaging.com"
                className="border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
              >
                <i className="ri-mail-line mr-2"></i>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>
    </section>
  );
};

export default FAQSection;
